import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Container,
    Typography,
    Paper,
    Grid,
    Card,
    CardContent,
    CardMedia,
    Button,
    Box,
    Divider,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import {
    ArrowForward as ArrowForwardIcon,
    Book as BookIcon,
    EditNote as EditNoteIcon,
    Security as SecurityIcon,
} from '@mui/icons-material';
import Seo from '../components/common/Seo';

function Guide() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const guideList = [
        {
            title: 'ルームシェアの始め方',
            description: 'ルームシェアを始める前に知っておきたい基本的な情報や準備について解説します。',
            icon: <BookIcon sx={{ fontSize: 60, color: theme.palette.primary.main }} />,
            link: '/guide/getting-started',
            bgColor: '#E3F2FD',
        },
        {
            title: '募集の書き方のコツ',
            description: '魅力的な募集投稿を作成するためのポイントやテクニックを紹介します。',
            icon: <EditNoteIcon sx={{ fontSize: 60, color: theme.palette.primary.main }} />,
            link: '/guide/how-to-post',
            bgColor: '#E8F5E9',
        },
        {
            title: '安全なルームシェアのために',
            description: 'ルームシェアを安全に楽しむための注意点や確認事項について解説します。',
            icon: <SecurityIcon sx={{ fontSize: 60, color: theme.palette.primary.main }} />,
            link: '/guide/safe-roomshare',
            bgColor: '#FFF8E1',
        },
    ];

    return (
        <>
            <Seo
                title="ルームシェアガイド"
                description="ルームシェアを始めるための基本情報や上手な募集の書き方、安全に利用するためのポイントをご紹介します。初めてのルームシェアも安心して始められます。"
                path="/guide"
                keywords="ルームシェアガイド,ルームシェアの始め方,募集の書き方,安全なルームシェア,ルームメイト"
            />
            <Container maxWidth="md">
                <Paper sx={{ p: { xs: 2, md: 4 }, mt: 4, mb: 4, borderRadius: 2 }}>
                    <Typography
                        variant="h4"
                        component="h1"
                        gutterBottom
                        sx={{
                            fontWeight: 'bold',
                            fontSize: { xs: '1.8rem', md: '2.5rem' }
                        }}
                    >
                        ルームシェアガイド
                    </Typography>

                    <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
                        ルームシェアを始める方も、経験者の方も、より快適なルームシェアライフのためのガイドをご用意しました。
                        安心して理想のルームシェアを実現するためのポイントを解説しています。
                    </Typography>

                    <Divider sx={{ mb: 4 }} />

                    <Grid container spacing={3}>
                        {guideList.map((guide, index) => (
                            <Grid item xs={12} sm={6} md={4} key={index}>
                                <Card
                                    sx={{
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                                        '&:hover': {
                                            transform: 'translateY(-5px)',
                                            boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)',
                                        },
                                        borderRadius: 2,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            p: 3,
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            backgroundColor: guide.bgColor
                                        }}
                                    >
                                        {guide.icon}
                                    </Box>
                                    <CardContent sx={{ flexGrow: 1 }}>
                                        <Typography gutterBottom variant="h6" component="h2">
                                            {guide.title}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                            {guide.description}
                                        </Typography>
                                        <Button
                                            component={RouterLink}
                                            to={guide.link}
                                            color="primary"
                                            endIcon={<ArrowForwardIcon />}
                                        >
                                            詳しく見る
                                        </Button>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>

                    <Box sx={{ mt: 6, p: 3, bgcolor: '#F5F5F5', borderRadius: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            お役立ち情報
                        </Typography>
                        <Typography variant="body2">
                            より詳しい情報やご質問は、<RouterLink to="/help" style={{ color: theme.palette.primary.main }}>ヘルプセンター</RouterLink>をご覧いただくか、
                            <RouterLink to="/contact" style={{ color: theme.palette.primary.main }}>お問い合わせフォーム</RouterLink>からお気軽にご連絡ください。
                        </Typography>
                    </Box>
                </Paper>
            </Container>
        </>
    );
}

export default Guide; 